import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Modal,
    InlineLoading,
    Button,
    InlineNotification,
} from "@carbon/react";
import { useTranslation } from "react-i18next";

const URL_RENEWAL_MARGIN_SECONDS = 60;
const RESUME_OVERLAP_SECONDS = 1;

const MediaPlaybackModal = ({
    open,
    onClose,
    onRetry,
    onPlaybackError,
    onRenewPlaybackUrl,
    playbackUrl,
    playbackType,
    playbackExpiresIn,
    loading,
    error,
    title,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const resumeAfterRenewalRef = useRef(null);
    const renewingRef = useRef(false);
    const renewalTimerRef = useRef(null);

    const [renewing, setRenewing] = useState(false);

    const clearRenewalTimer = useCallback(() => {
        if (renewalTimerRef.current) {
            window.clearTimeout(renewalTimerRef.current);
            renewalTimerRef.current = null;
        }
    }, []);

    const renewPlaybackUrl = useCallback(
        async (reason = "scheduled") => {
            if (
                !open ||
                !playbackUrl ||
                playbackType !== "mp4" ||
                typeof onRenewPlaybackUrl !== "function" ||
                renewingRef.current
            ) {
                return;
            }

            const video = videoRef.current;

            resumeAfterRenewalRef.current = video
                ? {
                      currentTime: Math.max(
                          0,
                          video.currentTime - RESUME_OVERLAP_SECONDS
                      ),
                      wasPaused: video.paused,
                      muted: video.muted,
                      volume: video.volume,
                      playbackRate: video.playbackRate || 1,
                  }
                : null;

            try {
                renewingRef.current = true;
                setRenewing(true);

                await onRenewPlaybackUrl();

                console.info(`URL de reproducción renovada: ${reason}`);
            } catch (renewError) {
                console.error("No se pudo renovar la URL de reproducción", renewError);

                if (typeof onPlaybackError === "function") {
                    onPlaybackError(t("media.renewalError"));
                }
            } finally {
                renewingRef.current = false;
                setRenewing(false);
            }
        },
        [
            open,
            playbackUrl,
            playbackType,
            onRenewPlaybackUrl,
            onPlaybackError,
            t,
        ]
    );

    useEffect(() => {
        if (!open && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.removeAttribute("src");
            videoRef.current.load();
        }

        if (!open) {
            clearRenewalTimer();
            resumeAfterRenewalRef.current = null;
            renewingRef.current = false;
            setRenewing(false);
        }
    }, [open, clearRenewalTimer]);

    useEffect(() => {
        if (!open || !playbackUrl || !videoRef.current) {
            return;
        }

        const video = videoRef.current;
        const resumeState = resumeAfterRenewalRef.current;

        video.muted = true;
        video.playbackRate = resumeState?.playbackRate || 1;

        const resumeAndPlay = () => {
            if (resumeState) {
                try {
                    video.currentTime = resumeState.currentTime;
                    video.muted = resumeState.muted;
                    video.volume = resumeState.volume;
                    video.playbackRate = resumeState.playbackRate || 1;
                } catch (seekError) {
                    console.warn(
                        "No se pudo restaurar exactamente el punto de reproducción",
                        seekError
                    );
                }

                resumeAfterRenewalRef.current = null;

                if (resumeState.wasPaused) {
                    return;
                }
            }

            const playPromise = video.play();

            if (playPromise !== undefined) {
                playPromise.catch((playError) => {
                    console.warn(
                        "No se pudo iniciar la reproducción automática",
                        playError
                    );
                });
            }
        };

        if (resumeState) {
            const onLoadedMetadata = () => {
                resumeAndPlay();
            };

            video.addEventListener("loadedmetadata", onLoadedMetadata, {
                once: true,
            });

            video.load();

            return () => {
                video.removeEventListener("loadedmetadata", onLoadedMetadata);
            };
        }

        resumeAndPlay();
    }, [open, playbackUrl]);

    useEffect(() => {
        clearRenewalTimer();

        if (
            !open ||
            !playbackUrl ||
            playbackType !== "mp4" ||
            !playbackExpiresIn ||
            typeof onRenewPlaybackUrl !== "function"
        ) {
            return;
        }

        const renewInSeconds = Math.max(
            10,
            playbackExpiresIn - URL_RENEWAL_MARGIN_SECONDS
        );

        renewalTimerRef.current = window.setTimeout(() => {
            renewPlaybackUrl("scheduled-before-expiration");
        }, renewInSeconds * 1000);

        return clearRenewalTimer;
    }, [
        open,
        playbackUrl,
        playbackType,
        playbackExpiresIn,
        onRenewPlaybackUrl,
        renewPlaybackUrl,
        clearRenewalTimer,
    ]);

    const handleVideoError = () => {
        if (!open) {
            return;
        }

        if (
            typeof onRenewPlaybackUrl === "function" &&
            playbackType === "mp4" &&
            !renewingRef.current
        ) {
            renewPlaybackUrl("video-error");
            return;
        }

        if (typeof onPlaybackError === "function") {
            onPlaybackError(t("media.browserPlaybackError"));
        }
    };

    return (
        <Modal
            open={open}
            onRequestClose={onClose}
            modalHeading={title || t("media.playbackTitle")}
            passiveModal
            size="lg"
        >
            {loading && (
                <InlineLoading
                    description={t("media.requestingPlayback")}
                    status="active"
                />
            )}

            {!loading && renewing && (
                <InlineLoading
                    description={t("media.renewingPlayback")}
                    status="active"
                />
            )}

            {!loading && error && (
                <>
                    <InlineNotification
                        kind="error"
                        title={t("media.loadVideoError")}
                        subtitle={error}
                        lowContrast
                        hideCloseButton
                    />

                    {typeof onRetry === "function" && (
                        <Button
                            kind="tertiary"
                            size="sm"
                            onClick={onRetry}
                        >
                            {t("common.retry")}
                        </Button>
                    )}
                </>
            )}

            {!loading && !error && playbackUrl && playbackType === "mp4" && (
                <video
                    ref={videoRef}
                    controls
                    autoPlay
                    muted
                    preload="metadata"
                    src={playbackUrl}
                    style={{
                        width: "100%",
                        maxHeight: "70vh",
                        background: "#000",
                    }}
                    onError={handleVideoError}
                />
            )}

            {!loading && !error && playbackUrl && playbackType === "hls" && (
                <InlineNotification
                    kind="warning"
                    title={t("media.hlsUnavailable")}
                    subtitle={t("media.hlsDescription")}
                    lowContrast
                    hideCloseButton
                />
            )}
        </Modal>
    );
};

export default MediaPlaybackModal;
